import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { DEFAULT_PAGE_SIZE } from "@/utils/utils";
import { useDebounce } from "@/hooks/use-debounce";
import useDisclosure from "@/hooks/use-disclosure";
import { PAYER_DEFAULT_SORT, PayerFilters } from "../payers-constant";

const INITIAL_FILTERS: PayerFilters = { payerType: null, status: null };
const FIRST_PAGE = 0;

export const usePayersControls = () => {
  const [debouncedSearch, setSearchKey, searchKey] = useDebounce("");
  const [filters, setFilters] = useState<PayerFilters>(INITIAL_FILTERS);
  const [isArchivedView, setIsArchivedView] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>(PAYER_DEFAULT_SORT);
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: FIRST_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const drawer = useDisclosure(false);

  const resetPage = useCallback(
    () => setPagination((prev) => ({ ...prev, page: FIRST_PAGE })),
    []
  );

  const onSearch = useCallback(
    (value: string) => {
      setSearchKey(value);
      resetPage();
    },
    [setSearchKey, resetPage]
  );

  const onFiltersApply = useCallback(
    (applied: Partial<PayerFilters>) => {
      setFilters({
        payerType: applied.payerType || null,
        status: applied.status || null,
      });
      resetPage();
    },
    [resetPage]
  );

  const onClearFilters = useCallback(() => {
    setSearchKey("");
    setFilters(INITIAL_FILTERS);
    resetPage();
  }, [setSearchKey, resetPage]);

  const onArchivedViewChange = useCallback(
    (value: boolean) => {
      setIsArchivedView(value);
      resetPage();
    },
    [resetPage]
  );

  return {
    searchKey,
    debouncedSearch,
    filters,
    isArchivedView,
    sortModel,
    pagination,
    drawer,
    hasActiveFilters:
      !!debouncedSearch || !!filters.payerType || !!filters.status,
    onSearch,
    onFiltersApply,
    onClearFilters,
    onArchivedViewChange,
    onSortModelChange: setSortModel,
    onPaginationChange: setPagination,
  };
};

export type PayersControls = ReturnType<typeof usePayersControls>;
